import React, { useEffect, useState } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import {
  Box, Tabs, Tab, FormControl, InputLabel, Select, MenuItem, TextField,
  SelectChangeEvent, Chip, Button, Typography, Tooltip, Link,
} from '@mui/material';
import MyTable from '../components/MyTable';
import { BalanceCompte, Compte, CompteCaisse, FamilleData } from '../models/models';
import { TableHeader } from '../constants/interfaces';
import FamilleAutoC from '../components/AutoComplete/FamilleAutoC';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCompteFilters, fetchComptes, clearCompteFilters,
  setBalanceFilters, fetchBalance, clearBalanceData, resetBalanceFilters,
  setCompteActiveTab, CompteTab,
} from '../reduxStore/compteSlice';
import { AppDispatch } from '../reduxStore/Store';
import CompteFilters from '../components/Filters/CompteFilters';
import { formatNumberWithSpaces } from '../utils/utils';
import { fetchEtatCaisse } from '../reduxStore/ReglementSlice';
import { FormatRapport, TypeRapport, GeneriqueDataType } from '../constants/enums';
import { useGenerateReport } from '../hooks/useGenerateReport';
import PrintMenuButton from '../components/PrintMenuButton';
import { getLabels } from '../constants/fieldLabels';
import { labels } from '../constants/labels';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BalanceIcon from '@mui/icons-material/Balance';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';

type TabKey = CompteTab;

/** Calcule le solde final d'une ligne de balance — arrondi à 2 décimales
 *  pour éviter les artefacts de précision flottante (ex: 18242999.810000002). */
const computeSolde = (b: BalanceCompte): number =>
  Math.round(
    (Number(b.solde_anterieur) + Number(b.total_facture) - Number(b.total_reglement)) * 100
  ) / 100;

const Comptes: React.FC = () => {
  const { comptes, compteFilters, total: totalCompte,
          solde_depart, solde_final,
          balanceData, totalBalance, balanceTotaux, balanceFilters,
          activeTab } = useSelector((state: any) => state.CompteSlice);
  const { compteCaisse, total: totalCaisse }           = useSelector((state: any) => state.ReglementSlice);
  const selectedDepartement                            = useSelector((state: any) => state.AppSlice.selectedDepartement);

  const dispatch = useDispatch<AppDispatch>();
  const { generateReport, generating } = useGenerateReport();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  // Catégorie sélectionnée (objet pour l'autocomplete) — type piloté par type_tiers.
  const [selectedCategorie, setSelectedCategorie] = useState<FamilleData | null>(null);

  // Type de catégorie associé au type de tiers courant (employé non catégorisable).
  const balanceCategorieType: GeneriqueDataType | null =
    balanceFilters.type_tiers === 'client'      ? GeneriqueDataType.CATEGORIE_CLIENT
    : balanceFilters.type_tiers === 'fournisseur' ? GeneriqueDataType.CATEGORIE_FOURNISSEUR
    : null;

  // Reset l'objet catégorie quand le filtre est vidé (reset / changement de type).
  useEffect(() => {
    if (balanceFilters.categorie_id == null) setSelectedCategorie(null);
  }, [balanceFilters.categorie_id]);

  // À l'ouverture de la page :
  //  - retour arrière (POP, ex. depuis un document ouvert par une écriture) → l'état est
  //    conservé (onglet, filtres, page) et la liste est rechargée : le document a pu
  //    être modifié entre-temps, le solde doit être à jour ;
  //  - toute autre arrivée (menu, lien) → écran vierge, comme avant.
  useEffect(() => {
    if (navigationType !== 'POP') {
      dispatch(clearCompteFilters());
      dispatch(setCompteActiveTab('detail'));
      return;
    }
    if (compteFilters.tiers_type === 'CAISSE') {
      if (compteFilters.caisse_id) dispatch(fetchEtatCaisse(compteFilters));
    } else if (compteFilters.tiers_id) {
      dispatch(fetchComptes(compteFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Fetch balance quand on entre dans l'onglet OU quand les filtres changent
  useEffect(() => {
    if (activeTab !== 'balance') return;
    dispatch(fetchBalance(balanceFilters));
    return () => { dispatch(clearBalanceData()); };
  }, [dispatch, activeTab, balanceFilters]);

  // ── Headers ────────────────────────────────────────────────────────────────
  const headersCompteTiers: TableHeader<Compte>[] = [
    { key: 'date',             label: 'Date' },
    {
      key: 'numero_operation',
      label: "Numéro d'opération",
      // Écriture issue d'un document → accès direct au document (route par id, jamais
      // par numéro). Règlement / opération diverse : pas de document, texte simple.
      render: (r) => {
        if (!r.document_vente_id) return r.numero_operation;
        return (
          <Tooltip title="Ouvrir le document">
            <Link
              component="button"
              type="button"
              underline="hover"
              sx={{ fontWeight: 600, textAlign: 'left' }}
              onClick={() => navigate(`/facturation/${r.document_vente_id}`)}
            >
              {r.numero_operation}
            </Link>
          </Tooltip>
        );
      },
    },
    { key: 'reference',        label: 'Référence' },
    { key: 'departement_nom',  label: 'Département' },
    { key: 'debit',            label: 'Débit',  align: 'right' },
    { key: 'credit',           label: 'Crédit', align: 'right' },
    { key: 'solde',            label: 'Solde',  align: 'right' },
  ];

  const headersCompteCaisse: TableHeader<CompteCaisse>[] = [
    { key: 'date_reglement',   label: 'Date' },
    { key: 'numero_reglement', label: "Numéro d'opération" },
    { key: 'reference',        label: 'Référence' },
    { key: 'debit',            label: 'Débit',  align: 'right' },
    { key: 'credit',           label: 'Crédit', align: 'right' },
    { key: 'solde',            label: 'Solde',  align: 'right' },
  ];

  const headersBalance: TableHeader<BalanceCompte>[] = [
    { key: 'nom',             label: 'Nom',          render: (r) => `${r.nom ?? ''}${r.prenom ? ' ' + r.prenom : ''}`.trim() },
    { key: 'telephone',       label: 'Téléphone' },
    { key: 'solde_anterieur', label: 'Solde antérieur', align: 'right' },
    { key: 'total_facture',   label: 'Total Facture',   align: 'right' },
    { key: 'total_reglement', label: 'Total Règlement', align: 'right' },
    {
      key: 'solde_final', label: 'Solde',
      render: (r) => {
        const s = computeSolde(r);
        return (
          <Chip
            label={formatNumberWithSpaces(s)}
            color={s > 0 ? 'error' : s < 0 ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
        );
      },
    },
  ];

  // ── Pagination ─────────────────────────────────────────────────────────────
  // Page du détail pilotée par les filtres du store (et non par l'état interne de
  // MyTable) : au retour d'un document, la table se remonte sur la page quittée.
  const detailPage = Math.floor(compteFilters.offset / compteFilters.limit);

  const handleDetailPageChange = (newPage: number, newRowsPerPage: number) => {
    const updated = { ...compteFilters, offset: newPage * newRowsPerPage, limit: newRowsPerPage };
    dispatch(setCompteFilters(updated));
    if (updated.tiers_type === 'CAISSE') dispatch(fetchEtatCaisse(updated));
    else                                  dispatch(fetchComptes(updated));
  };

  const handleBalancePageChange = (newPage: number, newRowsPerPage: number) => {
    dispatch(setBalanceFilters({ offset: newPage * newRowsPerPage, limit: newRowsPerPage }));
  };

  // ── Balance filters handlers ───────────────────────────────────────────────
  const handleTypeChange = (e: SelectChangeEvent<string>) => {
    dispatch(setBalanceFilters({ type_tiers: e.target.value as 'client' | 'fournisseur' | 'employe' }));
  };

  const handleDateChange = (field: 'date_debut' | 'date_fin') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setBalanceFilters({ [field]: e.target.value || null }));
    };

  // ── Type & titre actifs ────────────────────────────────────────────────────
  const isCaisse = compteFilters.tiers_type === 'CAISSE';

  const detailReportType = isCaisse
    ? TypeRapport.COMPTE_CAISSE
    : compteFilters.tiers_type === 'fournisseur'
      ? TypeRapport.COMPTE_FOURNISSEUR
      : compteFilters.tiers_type === 'employe'
        ? TypeRapport.COMPTE_EMPLOYE
        : TypeRapport.COMPTE_CLIENT;

  const balanceReportType = balanceFilters.type_tiers === 'fournisseur'
    ? TypeRapport.BALANCE_FOURNISSEUR
    : balanceFilters.type_tiers === 'employe'
      ? TypeRapport.BALANCE_EMPLOYE
      : TypeRapport.BALANCE_CLIENT;

  const reportType = activeTab === 'detail' ? detailReportType : balanceReportType;

  const pageTitle = activeTab === 'balance'
    ? (balanceFilters.type_tiers === 'fournisseur' ? 'Balance Fournisseurs'
      : balanceFilters.type_tiers === 'employe' ? 'Balance Employés'
      : 'Balance Clients')
    : isCaisse
      ? 'État de Caisse'
      : compteFilters.tiers_type === 'fournisseur'
        ? 'Comptes Fournisseurs'
        : compteFilters.tiers_type === 'employe'
          ? 'Comptes Employés'
          : 'Comptes Clients';

  // ── Print ──────────────────────────────────────────────────────────────────
  const handlePrint = (format: FormatRapport) => {
    if (activeTab === 'balance') {
      generateReport({
        type_rapport:   balanceReportType,
        format,
        parametres: {
          type_tiers:     balanceFilters.type_tiers,
          date_debut:     balanceFilters.date_debut,
          date_fin:       balanceFilters.date_fin,
          categorie_id:   balanceFilters.categorie_id,
          departement_id: selectedDepartement?.id ?? null,
        },
        departement_id: selectedDepartement?.id ?? null,
        fileName:       `${balanceReportType.toLowerCase()}_${format.toLowerCase()}`,
      });
      return;
    }
    const { limit, offset, ...filterParams } = compteFilters as any;
    generateReport({
      type_rapport:   detailReportType,
      format,
      parametres:     Object.keys(filterParams).length ? filterParams : null,
      departement_id: selectedDepartement?.id ?? null,
      fileName:       `${detailReportType.toLowerCase()}_${format.toLowerCase()}`,
    });
  };

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title={pageTitle}
        subtitle={
          activeTab === 'balance'
            ? `${totalBalance} tiers avec solde non nul`
            : undefined
        }
        icon={activeTab === 'balance' ? <BalanceIcon /> : <AccountBalanceIcon />}
        actions={
          <PrintMenuButton
            onPdf={()   => handlePrint(FormatRapport.PDF)}
            onExcel={() => handlePrint(FormatRapport.EXCEL)}
            loading={generating}
          />
        }
      />

      <AppSurface sx={{ mb: 2 }} padding={0}>
        <Tabs
          value={activeTab}
          onChange={(_, v: TabKey) => dispatch(setCompteActiveTab(v))}
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="detail"  label="Détail compte"     icon={<AccountBalanceIcon />} iconPosition="start" />
          <Tab value="balance" label="Balance par tiers" icon={<BalanceIcon />}        iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {activeTab === 'detail' ? (
            <CompteFilters />
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="balance-type-label">Type de tiers</InputLabel>
                <Select
                  labelId="balance-type-label"
                  label="Type de tiers"
                  value={balanceFilters.type_tiers}
                  onChange={handleTypeChange}
                >
                  <MenuItem value="client">{labels.client}</MenuItem>
                  <MenuItem value="fournisseur">{labels.fournisseur}</MenuItem>
                  <MenuItem value="employe">{labels.employe}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                type="date"
                label="Date début"
                value={balanceFilters.date_debut ?? ''}
                onChange={handleDateChange('date_debut')}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small"
                type="date"
                label="Date fin"
                value={balanceFilters.date_fin ?? ''}
                onChange={handleDateChange('date_fin')}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />

              {balanceCategorieType && (
                <Box sx={{ minWidth: 200 }}>
                  <FamilleAutoC
                    slot="main"
                    type={balanceCategorieType}
                    label="Catégorie"
                    selectedFamille={selectedCategorie}
                    onFamilleChange={(c) => {
                      setSelectedCategorie(c);
                      dispatch(setBalanceFilters({ categorie_id: c ? c.id : null }));
                    }}
                  />
                </Box>
              )}

              <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                Période vide = depuis le début. Les tiers à solde nul sont exclus.
              </Typography>

              <Button variant="outlined" color="secondary" size="small" onClick={() => dispatch(resetBalanceFilters())}>
                Réinitialiser
              </Button>
            </Box>
          )}
        </Box>
      </AppSurface>

      <AppSurface>
        {activeTab === 'detail' ? (
          isCaisse ? (
            <MyTable
              key="caisse"
              headers={headersCompteCaisse}
              data={compteCaisse}
              onPageChange={handleDetailPageChange}
              page={detailPage}
              pageSize={compteFilters.limit}
              actions={[]}
              totalRows={totalCaisse}
            />
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Chip
                  label={`Solde de départ : ${formatNumberWithSpaces(solde_depart)}`}
                  variant="outlined"
                  size="medium"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <MyTable
                key="detail"
                headers={headersCompteTiers}
                data={comptes}
                onPageChange={handleDetailPageChange}
                page={detailPage}
                pageSize={compteFilters.limit}
                actions={[]}
                totalRows={totalCompte}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Chip
                  label={`Solde final : ${formatNumberWithSpaces(solde_final)}`}
                  color={solde_final > 0 ? 'error' : solde_final < 0 ? 'success' : 'default'}
                  variant="filled"
                  size="medium"
                  sx={{ fontWeight: 700, fontSize: '0.95rem', px: 1 }}
                />
              </Box>
            </Box>
          )
        ) : (
          <Box>
            <MyTable
              key="balance"
              headers={headersBalance}
              data={balanceData}
              totalRows={totalBalance}
              onPageChange={handleBalancePageChange}
              defaultPageSize={balanceFilters.limit}
              size="small"
              showDetails
              fieldLabels={getLabels('BalanceCompte')}
            />
            {/* Totaux généraux — sur l'ensemble des tiers filtrés (pas seulement la page) */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
              <Chip
                label={`Total facture : ${formatNumberWithSpaces(balanceTotaux.total_facture)}`}
                variant="outlined" size="medium"
              />
              <Chip
                label={`Total règlement : ${formatNumberWithSpaces(balanceTotaux.total_reglement)}`}
                variant="outlined" size="medium"
              />
              <Chip
                label={`Solde général : ${formatNumberWithSpaces(balanceTotaux.solde_final)}`}
                color={balanceTotaux.solde_final > 0 ? 'error' : balanceTotaux.solde_final < 0 ? 'success' : 'default'}
                variant="filled" size="medium"
                sx={{ fontWeight: 700, fontSize: '0.95rem', px: 1 }}
              />
            </Box>
          </Box>
        )}
      </AppSurface>
    </AppPage>
  );
};

export default Comptes;
